import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Edit3, Plus, Search, Trash2 } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataGrid } from "@/components/ui/data-grid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createProperty,
  deleteProperty,
  listProperties,
  listTenants,
  updateProperty,
} from "@/services/dataService";
import { formatCurrency, toDateInputValue } from "@/lib/format";

function parseNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : undefined;
}

function dateToTs(value) {
  if (!value) return undefined;
  const ts = new Date(`${value}T12:00:00`).getTime();
  return Number.isNaN(ts) ? undefined : ts;
}

const EMPTY_FORM = {
  name: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  propertyType: "Single Family",
  units: "1",
  monthlyRent: "",
  mortgageLoanBalance: "",
  mortgageAPR: "",
  mortgageMonthlyPayment: "",
  mortgageDueDay: "",
  mortgageLender: "",
  mortgageLoanNumber: "",
  depositBank: "",
  titleCompany: "",
  closingDate: "",
  maturityDate: "",
  notes: "",
};

export default function Properties() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
  // null (closed) | "new" (create) | property object (edit)
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const tenantsQuery = useQuery({
    queryKey: ["tenants", userId],
    queryFn: () => listTenants(),
    enabled: Boolean(userId),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      selectedProperty === "new"
        ? createProperty(payload)
        : updateProperty({ id: selectedProperty._id, ...payload }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["properties", userId] });
      toast({ title: selectedProperty === "new" ? "Property added" : "Property updated" });
      setSelectedProperty(null);
    },
    onError: (error) =>
      toast({ title: "Could not save property", description: error.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProperty,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["properties", userId] });
      toast({ title: "Property deleted" });
    },
    onError: (error) =>
      toast({ title: "Could not delete property", description: error.message, variant: "destructive" }),
  });

  const tenantCountByProperty = useMemo(() => {
    const map = new Map();
    for (const tenant of tenantsQuery.data || []) {
      if (!tenant.isActive) continue;
      map.set(tenant.propertyId, (map.get(tenant.propertyId) || 0) + 1);
    }
    return map;
  }, [tenantsQuery.data]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return propertiesQuery.data || [];
    }
    return (propertiesQuery.data || []).filter((property) => {
      const haystack = [
        property.name,
        property.address,
        property.city,
        property.state,
        property.zipCode,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [propertiesQuery.data, searchTerm]);

  const quickCreateMutation = useMutation({
    mutationFn: createProperty,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["properties", userId] });
      toast({ title: "Property added" });
    },
    onError: (error) =>
      toast({ title: "Could not add property", description: error.message, variant: "destructive" }),
  });

  function handleQuickAdd(draft, reset) {
    const name = (draft.name || "").trim();
    if (!name) {
      toast({ title: "Enter a name or address first", variant: "destructive" });
      return;
    }
    quickCreateMutation.mutate(
      {
        name,
        address: name,
        city: "",
        state: "",
        zipCode: "",
        propertyType: "Single Family",
        units: 1,
        monthlyRent: parseNumber(draft.monthlyRent) || 0,
        mortgageMonthlyPayment: parseNumber(draft.mortgageMonthlyPayment),
      },
      { onSuccess: () => reset() }
    );
  }

  function confirmDelete(property) {
    if (
      window.confirm(
        `Delete "${property.name || property.address}"? Financial history and documents are kept, but this cannot be undone.`
      )
    ) {
      deleteMutation.mutate(property._id);
    }
  }

  const columns = [
    {
      key: "name",
      header: "Property",
      sortable: true,
      sortValue: (row) => row.name || row.address,
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{row.name || row.address}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[row.address, row.city, row.state].filter(Boolean).join(", ")}
          </p>
        </div>
      ),
    },
    {
      key: "monthlyRent",
      header: "Rent",
      align: "right",
      sortable: true,
      render: (row) => formatCurrency(row.monthlyRent),
    },
    {
      key: "mortgageMonthlyPayment",
      header: "Mortgage",
      align: "right",
      sortable: true,
      render: (row) =>
        row.mortgageMonthlyPayment ? formatCurrency(row.mortgageMonthlyPayment) : "—",
    },
    {
      key: "net",
      header: "Net / mo",
      align: "right",
      sortable: true,
      sortValue: (row) => (row.monthlyRent || 0) - (row.mortgageMonthlyPayment || 0),
      render: (row) => {
        const net = (row.monthlyRent || 0) - (row.mortgageMonthlyPayment || 0);
        return (
          <span
            className={
              net > 0
                ? "font-semibold text-emerald-600 dark:text-emerald-400"
                : net < 0
                  ? "font-semibold text-rose-600 dark:text-rose-400"
                  : "text-muted-foreground"
            }
          >
            {formatCurrency(net)}
          </span>
        );
      },
    },
    {
      key: "mortgageDueDay",
      header: "Draft day",
      align: "center",
      sortable: true,
      render: (row) => (row.mortgageDueDay ? `Day ${row.mortgageDueDay}` : "—"),
    },
    {
      key: "tenants",
      header: "Tenants",
      align: "center",
      sortValue: (row) => tenantCountByProperty.get(row._id) || 0,
      render: (row) => tenantCountByProperty.get(row._id) || 0,
    },
    {
      key: "mortgageLender",
      header: "Lender",
      render: (row) => row.mortgageLender || "—",
    },
  ];

  const totalRent = (filtered || []).reduce((sum, p) => sum + (p.monthlyRent || 0), 0);
  const totalMortgage = (filtered || []).reduce(
    (sum, p) => sum + (p.mortgageMonthlyPayment || 0),
    0
  );
  columns[0].footer = `${filtered.length} ${filtered.length === 1 ? "property" : "properties"}`;
  columns[1].footer = formatCurrency(totalRent);
  columns[2].footer = formatCurrency(totalMortgage);
  columns[3].footer = (
    <span className={totalRent - totalMortgage >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>
      {formatCurrency(totalRent - totalMortgage)}
    </span>
  );

  const quickAdd = {
    addLabel: "Add property",
    submitting: quickCreateMutation.isPending,
    onSubmit: handleQuickAdd,
    fields: [
      { key: "name", placeholder: "Name / address", autoFocus: true },
      { key: "monthlyRent", placeholder: "Rent", type: "number" },
      { key: "mortgageMonthlyPayment", placeholder: "Mortgage", type: "number" },
    ],
  };

  function openNew() {
    setForm(EMPTY_FORM);
    setSelectedProperty("new");
  }

  function openEdit(property) {
    setSelectedProperty(property);
    setForm({
      name: property.name || "",
      address: property.address || "",
      city: property.city || "",
      state: property.state || "",
      zipCode: property.zipCode || "",
      propertyType: property.propertyType || "Single Family",
      units: String(property.units ?? 1),
      monthlyRent: String(property.monthlyRent ?? 0),
      mortgageLoanBalance: property.mortgageLoanBalance ?? "",
      mortgageAPR: property.mortgageAPR ?? "",
      mortgageMonthlyPayment: property.mortgageMonthlyPayment ?? "",
      mortgageDueDay: property.mortgageDueDay ?? "",
      mortgageLender: property.mortgageLender || "",
      mortgageLoanNumber: property.mortgageLoanNumber || "",
      depositBank: property.depositBank || "",
      titleCompany: property.titleCompany || "",
      closingDate: toDateInputValue(property.closingDate),
      maturityDate: toDateInputValue(property.maturityDate),
      notes: property.notes || "",
    });
  }

  function handleSave() {
    if (!selectedProperty) return;
    const payload = {
      name: form.name.trim() || form.address.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zipCode: form.zipCode.trim(),
      propertyType: form.propertyType.trim() || "Single Family",
      units: parseNumber(form.units) || 1,
      monthlyRent: parseNumber(form.monthlyRent) || 0,
      mortgageLoanBalance: parseNumber(form.mortgageLoanBalance),
      mortgageAPR: parseNumber(form.mortgageAPR),
      mortgageMonthlyPayment: parseNumber(form.mortgageMonthlyPayment),
      mortgageDueDay: parseNumber(form.mortgageDueDay),
      mortgageLender: form.mortgageLender.trim() || undefined,
      mortgageLoanNumber: form.mortgageLoanNumber.trim() || undefined,
      depositBank: form.depositBank.trim() || undefined,
      titleCompany: form.titleCompany.trim() || undefined,
      closingDate: dateToTs(form.closingDate),
      maturityDate: dateToTs(form.maturityDate),
      notes: form.notes.trim() || undefined,
    };
    if (selectedProperty !== "new") {
      payload.clearMortgageLoanBalance = form.mortgageLoanBalance === "";
      payload.clearMortgageAPR = form.mortgageAPR === "";
      payload.clearMortgageMonthlyPayment = form.mortgageMonthlyPayment === "";
    }
    saveMutation.mutate(payload);
  }

  return (
    <PageLayout
      title="Properties"
      onRefresh={() => {
        propertiesQuery.refetch();
        tenantsQuery.refetch();
      }}
      isRefreshing={propertiesQuery.isFetching || tenantsQuery.isFetching}
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search properties by address or city"
              placeholder="Search by address or city"
              className="pl-9"
            />
          </div>
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Add with full details
          </Button>
        </div>

        {propertiesQuery.error ? (
          <Card>
            <CardContent className="py-6 text-sm text-red-600">
              {propertiesQuery.error.message}
            </CardContent>
          </Card>
        ) : (
          <DataGrid
            columns={columns}
            rows={filtered}
            getRowId={(row) => row._id}
            onRowClick={(row) => openEdit(row)}
            initialSort={{ key: "name", dir: "asc" }}
            quickAdd={quickAdd}
            emptyState={
              searchTerm
                ? "No matching properties."
                : "Type a name and rent below, then press Enter to add your first property."
            }
            rowActions={(row) => (
              <>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openEdit(row);
                  }}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Edit property"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    confirmDelete(row);
                  }}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-rose-600"
                  aria-label="Delete property"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          />
        )}

        <p className="px-1 text-xs text-muted-foreground">
          <Building2 className="mr-1 inline h-3.5 w-3.5" />
          Click any row to edit full mortgage, title, and loan details. Use the bottom row for quick
          entry.
        </p>
      </div>

      <Dialog open={Boolean(selectedProperty)} onOpenChange={(open) => !open && setSelectedProperty(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedProperty === "new" ? "Add Property" : "Edit Property"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                value={form.name || ""}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Address</Label>
              <Input
                value={form.address || ""}
                onChange={(event) => setForm((prev) => ({ ...prev, address: event.target.value }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>City</Label>
                <Input
                  value={form.city || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>State</Label>
                <Input
                  value={form.state || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>ZIP</Label>
                <Input
                  value={form.zipCode || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, zipCode: event.target.value }))}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Input
                  value={form.propertyType || ""}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, propertyType: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Units</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.units || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, units: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Monthly rent</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.monthlyRent || ""}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, monthlyRent: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Loan balance</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.mortgageLoanBalance}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mortgageLoanBalance: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>APR (%)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.mortgageAPR}
                  onChange={(event) => setForm((prev) => ({ ...prev, mortgageAPR: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Monthly mortgage payment</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.mortgageMonthlyPayment}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mortgageMonthlyPayment: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Mortgage drafts on day</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  placeholder="1"
                  value={form.mortgageDueDay}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mortgageDueDay: event.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Day of the month the bank drafts the payment — powers the cash-flow calendar.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Lender / servicer (optional)</Label>
                <Input
                  placeholder="e.g. Rocket Mortgage, PennyMac"
                  value={form.mortgageLender}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mortgageLender: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Loan number (optional)</Label>
                <Input
                  value={form.mortgageLoanNumber}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, mortgageLoanNumber: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Rent deposited into (optional)</Label>
                <Input
                  placeholder="e.g. Chase BIZ"
                  value={form.depositBank}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, depositBank: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label>Title company (optional)</Label>
                <Input
                  value={form.titleCompany}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, titleCompany: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Closing date (optional)</Label>
                <Input
                  type="date"
                  value={form.closingDate}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, closingDate: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Maturity date (optional)</Label>
                <Input
                  type="date"
                  value={form.maturityDate}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, maturityDate: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={form.notes || ""}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedProperty(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || !form.address?.trim() || !form.city?.trim()}
            >
              {saveMutation.isPending
                ? "Saving..."
                : selectedProperty === "new"
                  ? "Add property"
                  : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
